import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Star, TrendingUp, ArrowRight, Check, X, Share2 } from "lucide-react";
import { trackCTAClick } from "../lib/tracking";
import { buildPropertyAltText } from "../lib/helpers";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";

function fmt(v) {
  if (v == null) return "$0";
  return v < 0 ? `-$${Math.abs(Math.round(v)).toLocaleString()}` : `$${Math.round(v).toLocaleString()}`;
}

export default function DealOfTheDay({ variant = "full" }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/deal-of-the-day`)
      .then(r => { if (r.data?.deal) setData(r.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="animate-pulse rounded-xl border border-border bg-card p-6">
        <div className="h-5 bg-muted rounded w-1/3 mb-3" />
        <div className="h-4 bg-muted rounded w-2/3 mb-2" />
        <div className="h-32 bg-muted rounded" />
      </div>
    );
  }

  if (!data?.deal) return null;

  const deal = data.deal;
  const prop = deal.property || deal;
  const score = deal.deal_score || 0;
  const headline = data.headline || `Today's Top Pick`;
  const summary = data.summary || "";

  const scoreColor = score >= 75 ? "text-emerald-500" : score >= 50 ? "text-amber-500" : "text-red-500";
  const scoreBg = score >= 75 ? "bg-emerald-500" : score >= 50 ? "bg-amber-500" : "bg-red-500";

  const handleClick = () => {
    trackCTAClick("deal_of_the_day", `/dashboard?property=${prop.id}`);
    navigate(`/dashboard?property=${prop.id}`);
  };

  const handleShare = (e) => {
    e.stopPropagation();
    const url = `${window.location.origin}/dashboard?property=${prop.id}`;
    if (navigator.share) {
      navigator.share({ title: headline, text: summary, url });
    } else {
      navigator.clipboard.writeText(url);
    }
  };

  if (variant === "compact") {
    return (
      <div
        onClick={handleClick}
        className="relative p-4 rounded-xl border-2 border-amber-500/30 bg-gradient-to-r from-amber-500/5 to-transparent cursor-pointer hover:border-amber-500/50 transition-all group"
        data-testid="deal-of-the-day-compact"
      >
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
            <Star className="w-5 h-5 text-amber-500" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] text-amber-500 uppercase tracking-wider font-semibold mb-0.5">Deal of the Day</p>
            <p className="text-sm font-bold truncate">{prop.address || `${prop.city}, ${prop.state}`}</p>
            <p className="text-xs text-muted-foreground">{fmt(prop.price)} &middot; {fmt(deal.cash_flow_monthly)}/mo &middot; {score}/100</p>
          </div>
          <ArrowRight className="w-4 h-4 text-amber-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-1" />
        </div>
      </div>
    );
  }

  return (
    <Card
      className="overflow-hidden cursor-pointer border-2 border-amber-500/30 hover:border-amber-500/50 transition-all"
      onClick={handleClick}
      data-testid="deal-of-the-day"
    >
      <div className="relative">
        {prop.image_url && (
          <img src={prop.image_url} alt={buildPropertyAltText(prop, data?.deal)} className="w-full h-48 object-cover" loading="lazy" />
        )}
        <div className="absolute top-3 left-3">
          <Badge className="bg-amber-500 text-white border-0 text-xs font-bold flex items-center gap-1">
            <Star className="w-3 h-3 fill-white" /> Deal of the Day
          </Badge>
        </div>
        <div className="absolute top-3 right-3">
          <button onClick={handleShare} className="h-7 w-7 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center" data-testid="dotd-share-btn">
            <Share2 className="w-3.5 h-3.5 text-white" />
          </button>
        </div>
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent pt-8 pb-3 px-4">
          <span className="font-mono text-2xl font-bold text-white">{fmt(prop.price)}</span>
        </div>
      </div>

      <CardContent className="p-4">
        <div className="mb-3">
          <h3 className="font-bold text-base mb-0.5" data-testid="dotd-headline">{headline}</h3>
          <p className="text-xs text-muted-foreground">{prop.address} &middot; {prop.city}, {prop.state} {prop.zip_code}</p>
          {summary && <p className="text-xs text-muted-foreground mt-1 italic">{summary}</p>}
        </div>

        <div className="grid grid-cols-4 gap-2 mb-3">
          <div>
            <p className="text-[9px] text-muted-foreground uppercase">Rent</p>
            <p className="font-mono text-sm font-bold text-emerald-500">{fmt(deal.estimated_monthly_rent)}</p>
          </div>
          <div>
            <p className="text-[9px] text-muted-foreground uppercase">Cash Flow</p>
            <p className={`font-mono text-sm font-bold ${deal.cash_flow_monthly >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>{fmt(deal.cash_flow_monthly)}</p>
          </div>
          <div>
            <p className="text-[9px] text-muted-foreground uppercase">Cap Rate</p>
            <p className="font-mono text-sm font-bold">{(deal.cap_rate || 0).toFixed(1)}%</p>
          </div>
          <div>
            <p className="text-[9px] text-muted-foreground uppercase">Score</p>
            <p className={`font-mono text-sm font-bold ${scoreColor}`}>{score}/100</p>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-[10px]">
            <span className={deal.one_percent_rule ? "text-emerald-500 flex items-center gap-0.5" : "text-muted-foreground/40 flex items-center gap-0.5"}>
              {deal.one_percent_rule ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />} 1% Rule
            </span>
            <span className={deal.fifty_percent_rule ? "text-emerald-500 flex items-center gap-0.5" : "text-muted-foreground/40 flex items-center gap-0.5"}>
              {deal.fifty_percent_rule ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />} 50% Rule
            </span>
          </div>
          <Button size="sm" className="h-7 text-xs bg-amber-500 hover:bg-amber-600 text-white" data-testid="dotd-view-btn">
            View Full Analysis <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </div>

        {/* Score bar */}
        <div className="mt-3 pt-2 border-t border-border/30">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[9px] text-muted-foreground uppercase tracking-wider">Deal Score</span>
            <span className={`font-mono text-xs font-bold ${scoreColor}`}>{score}/100</span>
          </div>
          <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
            <div className={`h-full rounded-full ${scoreBg}`} style={{ width: `${score}%` }} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
